import type { FC } from 'react'
import { Navigate, type RouteObject } from 'react-router'
import { appRoutes } from '#app/navigation/routes/routes'
import { RoutingError } from '#features/ErrorHandling/RoutingError'
import { makeLocationFromViewId } from './components/ConfigurationNavigation'
import { useActiveConfiguration } from './components/useActiveConfiguration'

const ConfigurationRedirect: FC = () => {
  const configuration = useActiveConfiguration()
  const result = makeLocationFromViewId(configuration, configuration?.step.view)
  if (!result) {
    return undefined
  }
  return <Navigate to={result.result} />
}

export const configurationRoutes = {
  path: 'configuration',
  lazy: async () => ({ Component: (await import('../ConfigurationRootPage')).ConfigurationRootPage }),
  children: [
    {
      index: true,
      lazy: async () => ({ Component: (await import('./NewConfigurationPage')).NewConfigurationPage }),
    },
    {
      path: ':configurationId',
      Component: ConfigurationRedirect,
    },
    {
      path: appRoutes['configuration/error'].route,
      lazy: async () => ({ Component: (await import('./ConfigurationStepErrorPage')).ConfigurationStepErrorPage }),
    },

    {
      path: appRoutes['configuration/copy'].route,
      lazy: async () => ({ Component: (await import('./CopyConfigurationPage')).CopyConfigurationPage }),
    },
    // step-0
    {
      path: appRoutes['configuration/step-0'].route,
      children: [
        {
          index: true,
          lazy: async () => ({ Component: (await import('./ConfigurationLoadingPage')).ConfigurationLoadingPage }),
        },
        {
          path: appRoutes['configuration/step-0/landing'].route,
          lazy: async () => ({ Component: (await import('./LandingConfigurationPage')).LandingConfigurationPage }),
        },
        {
          path: appRoutes['configuration/step-0/history'].route,
          lazy: async () => ({ Component: (await import('./LandingConfigurationPage')).LandingConfigurationPage }),
        },
      ],
    },

    // step-1
    {
      path: appRoutes['configuration/step-1'].route,
      children: [
        {
          index: true,
          lazy: async () => ({ Component: (await import('./GeneralConfigurationPage')).GeneralConfigurationPage }),
        },
        {
          path: appRoutes['configuration/step-1/history'].route,
          lazy: async () => ({ Component: (await import('./GeneralConfigurationPage')).GeneralConfigurationPage }),
        },
      ],
    },

    // step-2
    {
      path: appRoutes['configuration/step-2'].route,
      lazy: async () => ({ Component: (await import('./DetailedStepPage')).DetailedStepPage }),
      children: [
        // all of these routes are overlays now
        {
          index: true,
          lazy: async () => ({ Component: (await import('./Details/DetailsView')).DetailsView }),
        },
        {
          path: appRoutes['configuration/step-2/history'].route,
          lazy: async () => ({ Component: (await import('./Details/DetailsView')).DetailsView }),
        },
        {
          path: appRoutes['configuration/step-2/print'].route,
          lazy: async () => ({ Component: (await import('./Details/DetailsView')).DetailsView }),
        },
        {
          path: appRoutes['configuration/step-2/compare'].route,
          lazy: async () => ({ Component: (await import('./Details/DetailsView')).DetailsView }),
        },
        {
          path: appRoutes['configuration/step-2/tire-editor'].route,
          lazy: async () => ({ Component: (await import('./Details/DetailsView')).DetailsView }),
        },
        // these are individual views
        {
          path: appRoutes['configuration/step-2/build-verification'].route,
          lazy: async () => ({ Component: (await import('./BuildVerification/BuildVerificationView')).Step2BuildVerificationView }),
        },
        {
          path: appRoutes['configuration/step-2/technical-data'].route,
          lazy: async () => ({ Component: (await import('./TechnicalInformationPage')).Step2TechnicalInformationPage }),
        },
        {
          path: appRoutes['configuration/step-2/optional-equipment'].route,
          lazy: async () => ({ Component: (await import('./OptionalEquipment/OptionalEquipment')).Step2OptionalEquipmentView }),
        },
      ],
    },

    // step-3
    {
      path: appRoutes['configuration/step-3'].route,
      lazy: async () => ({ Component: (await import('./SummaryConfigurationPage')).SummaryConfigurationPage }),
      children: [
        {
          index: true,
          lazy: async () => ({ Component: (await import('./SummaryStep/SummaryPrintView')).SummaryPrintView }),
        },
        {
          path: appRoutes['configuration/step-3/compare'].route,
          lazy: async () => ({ Component: (await import('./SummaryStep/SummaryPrintView')).SummaryPrintView }),
        },
        {
          path: appRoutes['configuration/step-3/changes'].route,
          lazy: async () => ({ Component: (await import('./SummaryStep/SummaryChangeView')).SummaryChangeView }),
        },
        {
          path: appRoutes['configuration/step-3/technical-data'].route,
          lazy: async () => ({ Component: (await import('./SummaryStep/SummaryTechnicalDataView')).SummaryTechnicalDataView }),
        },
        {
          path: appRoutes['configuration/step-3/build-verification'].route,
          lazy: async () => ({ Component: (await import('./SummaryStep/SummaryBuildVerificationView')).SummaryBuildVerificationView }),
        },
        {
          path: appRoutes['configuration/step-3/submitted'].route,
          lazy: async () => ({ Component: (await import('./SummaryStep/ConfigurationSubmittedPage')).ConfigurationSubmittedPage }),
        },
      ],
    },

    // catch-all config 404
    {
      path: '*',
      element: (
        <RoutingError
          error={{
            status: 404,
            statusText: 'Not found',
            data: null,
          }}
        />
      ),
    },
  ],
} satisfies RouteObject
